import type { MouseEvent as ReactMouseEvent, RefObject } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import type { TextData } from '../dto/EntityRepository';
import { useTextListMarqueeStore } from '../store/textListMarqueeStore';
import {
  MARQUEE_GESTURE_MIN_PX,
  type MarqueeDragState,
  collectTextsInMarquee,
  marqueeDragToBox,
} from '../util/textListMarqueeUtils';

export interface UseTextListMarqueeOptions {
  textList: TextData[];
  textListCompRef: RefObject<HTMLElement | null>;
  commitMarqueeHitTextsPreview: () => void;
  enabled: boolean;
}

export function useTextListMarquee({
  textList,
  textListCompRef,
  commitMarqueeHitTextsPreview,
  enabled,
}: UseTextListMarqueeOptions): {
  handleTextListMouseDown: (e: ReactMouseEvent<HTMLElement>) => void;
  handleTextListClickCapture: (e: ReactMouseEvent<HTMLElement>) => void;
  marqueeBox: ReturnType<typeof marqueeDragToBox>;
  marqueeSelecting: boolean;
} {
  const setHitTexts = useTextListMarqueeStore((s) => s.setHitTexts);
  const clearHitTexts = useTextListMarqueeStore((s) => s.clearHitTexts);

  const marqueeExceededThresholdRef = useRef(false);
  const suppressNextRowClickRef = useRef(false);
  const [marqueeDrag, setMarqueeDrag] = useState<MarqueeDragState | null>(null);

  useLayoutEffect(() => {
    if (marqueeDrag === null) {
      if (enabled) {
        commitMarqueeHitTextsPreview();
      }
      return;
    }
    if (!enabled) {
      return;
    }
    const el = textListCompRef.current;
    const box = marqueeDragToBox(marqueeDrag);
    if (!el || !box) {
      clearHitTexts();
      return;
    }
    setHitTexts(collectTextsInMarquee(el, box, textList));
  }, [enabled, marqueeDrag, textList, setHitTexts, clearHitTexts, commitMarqueeHitTextsPreview]);

  useLayoutEffect(() => {
    if (enabled) return;
    clearHitTexts();
    setMarqueeDrag(null);
  }, [enabled, clearHitTexts]);

  const handleTextListClickCapture = useCallback((e: ReactMouseEvent<HTMLElement>) => {
    if (!suppressNextRowClickRef.current) return;
    suppressNextRowClickRef.current = false;
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleTextListMouseDown = useCallback(
    (e: ReactMouseEvent<HTMLElement>) => {
      if (!enabled) return;
      if (e.button !== 0) return;
      const target = e.target as HTMLElement;
      if (target.closest('[data-suppress-marquee]')) return;

      const el = textListCompRef.current;
      if (!el) return;

      e.preventDefault();
      marqueeExceededThresholdRef.current = false;
      const rect = el.getBoundingClientRect();
      const ox = e.clientX - rect.left + el.scrollLeft;
      const oy = e.clientY - rect.top + el.scrollTop;

      const onMove = (ev: globalThis.MouseEvent) => {
        const r = el.getBoundingClientRect();
        const cx = ev.clientX - r.left + el.scrollLeft;
        const cy = ev.clientY - r.top + el.scrollTop;
        if (
          Math.abs(cx - ox) >= MARQUEE_GESTURE_MIN_PX ||
          Math.abs(cy - oy) >= MARQUEE_GESTURE_MIN_PX
        ) {
          marqueeExceededThresholdRef.current = true;
        }
        setMarqueeDrag((prev) =>
          prev ? { ...prev, currentX: cx, currentY: cy } : null
        );
      };

      const onUp = () => {
        if (marqueeExceededThresholdRef.current) {
          suppressNextRowClickRef.current = true;
        }
        marqueeExceededThresholdRef.current = false;
        setMarqueeDrag(null);
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };

      setMarqueeDrag({
        originX: ox,
        originY: oy,
        currentX: ox,
        currentY: oy,
      });
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [enabled, textListCompRef]
  );

  const marqueeBox = marqueeDragToBox(marqueeDrag);

  return {
    handleTextListMouseDown,
    handleTextListClickCapture,
    marqueeBox,
    marqueeSelecting: !!marqueeDrag,
  };
}
